import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import i18n from '@/i18n'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresGuest: true, title: 'auth.login' },
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/auth/AuthCallbackView.vue'),
    meta: { title: 'auth.processingCallback' },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, title: 'navigation.dashboard' },
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: () => import('@/views/booking/CalendarView.vue'),
    meta: { requiresAuth: true, title: 'navigation.calendar' },
  },
  {
    path: '/bookings',
    name: 'BookingList',
    component: () => import('@/views/booking/BookingListView.vue'),
    meta: { requiresAuth: true, title: 'navigation.myBookings' },
  },
  {
    path: '/bookings/create',
    name: 'BookingCreate',
    component: () => import('@/views/booking/BookingCreateView.vue'),
    meta: { requiresAuth: true, title: 'booking.createBooking' },
  },
  {
    path: '/bookings/:id',
    name: 'BookingDetail',
    component: () => import('@/views/booking/BookingDetailView.vue'),
    meta: { requiresAuth: true, title: 'booking.bookingDetails' },
  },
  {
    path: '/session',
    name: 'Session',
    component: () => import('@/views/session/SessionView.vue'),
    meta: { requiresAuth: true, title: 'navigation.activeSession' },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/user/ProfileView.vue'),
    meta: { requiresAuth: true, title: 'profile.title' },
  },
  {
    path: '/library/services',
    name: 'LibraryServices',
    component: () => import('@/views/library/LibraryServicesView.vue'),
    meta: { requiresAuth: true, title: 'library.services.title' },
  },
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard',
      },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/AdminDashboardView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.navigation.dashboard' },
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/admin/AdminUsersView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.users.title' },
      },
      {
        path: 'users/create',
        name: 'AdminUserCreate',
        component: () => import('@/views/admin/AdminUserCreateView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.users.createUser' },
      },
      {
        path: 'users/:id',
        name: 'AdminUserDetail',
        component: () => import('@/views/admin/AdminUserDetailView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.users.userDetails' },
      },
      {
        path: 'users/:id/edit',
        name: 'AdminUserEdit',
        component: () => import('@/views/admin/AdminUserEditView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.users.editUser' },
      },
      {
        path: 'machines',
        name: 'AdminMachines',
        component: () => import('@/views/admin/AdminMachinesView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'machine.title' },
      },
      {
        path: 'machines/create',
        name: 'AdminMachineCreate',
        component: () => import('@/views/admin/AdminMachineCreateView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'machine.createMachine' },
      },
      {
        path: 'machines/:id',
        name: 'AdminMachineDetail',
        component: () => import('@/views/admin/AdminMachineDetailView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'machine.machineDetails' },
      },
      {
        path: 'machines/:id/edit',
        name: 'AdminMachineEdit',
        component: () => import('@/views/admin/AdminMachineEditView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'machine.editMachine' },
      },
      {
        path: 'bookings',
        name: 'AdminBookings',
        component: () => import('@/views/admin/AdminBookingsView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.navigation.bookings' },
      },
      {
        path: 'bookings/calendar',
        name: 'AdminBookingCalendar',
        component: () => import('@/views/admin/AdminBookingCalendarView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'navigation.calendar' },
      },
      {
        path: 'bookings/create',
        name: 'AdminBookingCreate',
        component: () => import('@/views/admin/AdminBookingCreateView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'booking.createBooking' },
      },
      {
        path: 'bookings/:id',
        name: 'AdminBookingDetail',
        component: () => import('@/views/admin/AdminBookingDetailView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'booking.bookingDetails' },
      },
      {
        path: 'bookings/:id/edit',
        name: 'AdminBookingEdit',
        component: () => import('@/views/admin/AdminBookingEditView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'booking.editBooking' },
      },
      {
        path: 'sessions',
        name: 'AdminSessions',
        component: () => import('@/views/admin/AdminSessionsView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.sessions.title' },
      },
      {
        path: 'sessions/history',
        name: 'AdminSessionHistory',
        component: () => import('@/views/admin/AdminSessionHistoryView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.breadcrumbs.history' },
      },
      {
        path: 'reports',
        name: 'AdminReports',
        component: () => import('@/views/admin/AdminReportsView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.navigation.reports' },
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('@/views/admin/AdminSettingsView.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'admin.navigation.settings' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'errors.pageNotFound' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0 }
  },
})

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const token = localStorage.getItem('auth_token')
  const isAuthenticated = !!token

  // Update page title
  if (to.meta.title) {
    const translated = i18n.global.t(to.meta.title as string)
    const appName = i18n.global.t('common.appName')
    document.title = `${translated} — ${appName}`
  }

  // Check auth requirements
  if (to.meta.requiresAuth && !isAuthenticated) {
    // Redirect to login with return URL
    next({
      name: 'Login',
      query: { redirect: to.fullPath },
    })
    return
  }

  if (to.meta.requiresGuest && isAuthenticated) {
    // Already logged in, redirect to dashboard
    next({ name: 'Dashboard' })
    return
  }

  // Check admin requirements
  if (to.meta.requiresAdmin && isAuthenticated) {
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()

    if (!authStore.user) {
      try {
        await authStore.refreshUser()
      } catch (error) {
        console.error('Failed to load user data:', error)
        next({ name: 'Login', query: { redirect: to.fullPath } })
        return
      }
    }

    if (!authStore.isAdmin) {
      // Not an admin, redirect to dashboard
      console.warn('User attempted to access admin route without admin privileges')
      next({ name: 'Dashboard' })
      return
    }
  }

  next()
})

export default router
